import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import {
  getBusinessRelationsByMultipleStatus,
  getBusinessRelationsForUser,
  getOwnCompanyProducts,
} from 'lib/api';
import FilterComponent from 'lib/common/FilterNonSearch';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import {
  showAlert,
  validateCompanyTypes,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { RelationsStats, RelationStatus } from '../../types/businessRelations';
import AddRelation from './components/AddRelation';
import ConnectBusiness from './components/ConnectBusiness';
import DatahubReport from './components/DatahubReport';
import EditBusiness from './components/EditBusiness';
import EditConnection from './components/EditConnection';
import FundDialog from './components/FundDialog';
import OperativeSummary from './components/OperativeSummary';
import ShowConnections from './components/ShowConections';
import StatusInfoDialog from './components/StatusInfoDialog';

const nullCompanyState = {
  identifier: '',
  password: '',
  businessName: '',
  active: null,
};
export interface BusinessRelation {
  id?: number;
  identifier: string;
  password?: string;
  businessName: string;
  active?: boolean | null;
  status?: RelationStatus;
  statusLabel?: string;
  operativeSummary?: Record<string, boolean> | null;
}

const Companies: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [openShowConnections, setOpenShowConnections] = useState(false);
  const [connection, setConnection] = useState({});
  const [filteredBusinesses, setFilteredBusinesses] = useState<
    BusinessRelation[]
  >([]);
  const [dataCompanies, setDataCompanies] = useState<{
    relations: BusinessRelation[];
    ivaContainers: any[];
  }>({ relations: [], ivaContainers: [] });
  const [stats, setStats] = useState<RelationsStats>({
    [RelationStatus.ACTIVE]: 0,
    [RelationStatus.INACTIVE]: 0,
    [RelationStatus.DEACTIVATED]: 0,
    [RelationStatus.SUSPENDED]: 0,
    [RelationStatus.DELETED]: 0,
    total: 0,
  });
  const [hasOperativeSummary, setHasOperativeSummary] = useState(false);

  // Estado para filtros activos
  const [activeFilters, setActiveFilters] = useState<RelationStatus[]>([]);

  const [openFundDialog, setOpenFundDialog] = useState(false);
  const [openStatusInfoDialog, setOpenStatusInfoDialog] = useState(false);
  const [openDatahubReportDialog, setOpenDatahubReportDialog] = useState(false);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const [selectedBusiness, setSelectedBusiness] =
    useState<BusinessRelation>(nullCompanyState);

  const buttonsData: CustomButtonProps[] = [];

  buttonsData.push({
    name: 'Filtro',
    action: () => {},
    color: 'primary',
    customButton: (
      <FilterComponent
        data={dataCompanies.relations}
        filterFields={[
          { key: 'businessName', filterName: 'Nombre' },
          { key: 'identifier', filterName: 'Rut' },
          ...(validatePermissions(user, 'businesses_read_all')
            ? [
                {
                  key: 'users',
                  filterName: 'Ejecutivos',
                  type: 'includes',
                },
              ]
            : []),
        ]}
        setFilteredData={setFilteredBusinesses}
      />
    ),
  });

  if (validatePermissions(user, 'businesses_create_new'))
    buttonsData.push({
      action: () => handleOpenAddDialog(),
      name: 'Agregar',
      color: 'primary',
    });

  const handleNavigationSii = (id: number) => {
    const business = dataCompanies.relations.find(
      (x: BusinessRelation) => x.id === id,
    );
    if (!business) return showAlert(dispatch, 'error', 'Ha ocurrido un error');

    if (!business.active) {
      window.location.href = `/companies/${id}/sii/taxFolder`;
    } else {
      if (validatePermissions(user, 'sii_read_profile')) {
        window.location.href = `/companies/${id}/sii/summary`;
      } else {
        window.location.href = `/companies/${id}/tgr/debts`;
      }
    }
  };

  const handleOpenAddDialog = () => {
    setSelectedBusiness(nullCompanyState);
    setOpenAddDialog(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (e.target.name === 'identifier' || e.target.name === 'businessName') {
      value = value.toUpperCase();
    }
    setSelectedBusiness((prev: BusinessRelation) => ({
      ...prev,
      [e.target.name]: value,
    }));
  };

  const fetchRelations = async () => {
    setLoading(true);
    try {
      const results = await getBusinessRelationsByMultipleStatus(dispatch, [
        RelationStatus.ACTIVE,
        RelationStatus.INACTIVE,
        RelationStatus.DEACTIVATED,
      ]);

      let relations = [];
      if (Array.isArray(results)) {
        relations = results;
      } else if (results?.relations && Array.isArray(results.relations)) {
        relations = results.relations;
      } else if (results?.data && Array.isArray(results.data)) {
        relations = results.data;
      }

      const formattedResults = {
        relations: relations,
        ivaContainers: [],
      };

      setDataCompanies(formattedResults);
      setFilteredBusinesses(formattedResults.relations);

      const newStats: RelationsStats = results?.stats || {
        [RelationStatus.ACTIVE]: 0,
        [RelationStatus.INACTIVE]: 0,
        [RelationStatus.DEACTIVATED]: 0,
        [RelationStatus.SUSPENDED]: 0,
        [RelationStatus.DELETED]: 0,
        total: 0,
      };

      setStats(newStats);
      setHasOperativeSummary(results.hasOperativeSummary);
    } catch (error) {
      console.error('Error fetching business relations:', error);

      const results = await getBusinessRelationsForUser(dispatch);
      setDataCompanies(results || []);
      setFilteredBusinesses(results.relations);
      setHasOperativeSummary(results.hasOperativeSummary);
    }
    setLoading(false);
  };

  const fetchCompanyProducts = async () => {
    try {
      const availableProducts = await getOwnCompanyProducts(dispatch);
      setAvailableProducts(availableProducts.map((x: any) => x.typeId));
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    void fetchRelations();
    void fetchCompanyProducts();
  }, []);

  const handleStatusFilter = (status: RelationStatus) => {
    setActiveFilters((prev) => {
      if (prev.includes(status)) {
        return prev.filter((filter) => filter !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const applyFilters = () => {
    if (activeFilters.length === 0) {
      setFilteredBusinesses(dataCompanies.relations);
    } else {
      const filtered = dataCompanies.relations.filter(
        (relation) =>
          relation.status && activeFilters.includes(relation.status),
      );
      setFilteredBusinesses(filtered);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [activeFilters, dataCompanies.relations]);

  const getChipProps = (status: RelationStatus) => {
    const isActive = activeFilters.includes(status);
    const count = stats[status];

    const statusConfig = {
      [RelationStatus.ACTIVE]: { color: 'success', label: 'Conectadas' },
      [RelationStatus.INACTIVE]: { color: 'default', label: 'No Conectadas' },
      [RelationStatus.DEACTIVATED]: {
        color: 'warning',
        label: 'Desconectadas',
      },
      [RelationStatus.SUSPENDED]: { color: 'warning', label: 'Por Eliminar' },
      [RelationStatus.DELETED]: { color: 'error', label: 'Eliminadas' },
    };

    const config = statusConfig[status];

    return {
      label: `${count} ${config.label}`,
      color: config.color as 'success' | 'default' | 'warning' | 'error',
      variant: (isActive ? 'filled' : 'outlined') as 'filled' | 'outlined',
      onClick: () => handleStatusFilter(status),
      onDelete: isActive ? () => handleStatusFilter(status) : undefined,
      deleteIcon: isActive ? <CloseIcon /> : undefined,
      clickable: true,
      size: 'small' as const,
    };
  };

  const handleOpenEditDialog = (id: number) => {
    const business = dataCompanies.relations.find(
      (x: BusinessRelation) => x.id === id,
    );
    if (!business) return showAlert(dispatch, 'error', 'Ha ocurrido un error');
    setSelectedBusiness(business);
    setOpenEditDialog(true);
  };

  const canSeeBusiness = () => {
    const siiPermission = validatePermissions(user, 'sii_read_profile');
    const tgrPersmission = validatePermissions(user, 'tgr_read');
    const clientPermission = validatePermissions(user, 'client_read');
    const scoringPermission = validatePermissions(user, 'scoring_read');

    if (
      siiPermission ||
      tgrPersmission ||
      clientPermission ||
      scoringPermission
    )
      return true;
    return false;
  };

  const canEditBusiness = () => {
    const permission = validatePermissions(user, 'businesses_create_new');
    if (permission) return true;
    return false;
  };

  const handleOpenFundDialog = (id: number) => {
    const business = dataCompanies.relations.find(
      (x: BusinessRelation) => x.id === id,
    );
    if (!business) return alert('Ha ocurrido un error');

    setSelectedBusiness(business);
    setOpenFundDialog(true);
  };

  const canEnableToFund = () => {
    if (!validateCompanyTypes(user, 'Multiverse')) {
      return false;
    }
    return validatePermissions(user, 'multiverse_shareBusiness_to_funds');
  };

  const handleOpenDatahubReportDialog = (id: number) => {
    const business = filteredBusinesses.find(
      (x: BusinessRelation) => x.id === id,
    );
    if (!business) return showAlert(dispatch, 'error', 'Ha ocurrido un error');
    setSelectedBusiness(business);
    setOpenDatahubReportDialog(true);
  };

  const actions = [
    {
      action: handleNavigationSii,
      actionLabel: 'Ver',
      canShow: canSeeBusiness,
    },
    {
      action: handleOpenEditDialog,
      actionLabel: 'Editar',
      canShow: canEditBusiness,
    },
    {
      action: handleOpenDatahubReportDialog,
      actionLabel: 'Exportar Reporte',
    },
    {
      action: handleOpenFundDialog,
      actionLabel: 'Conexión a fondo',
      canShow: canEnableToFund,
    },
  ];
  const handleOpenAdd = () => {
    setAddOpen(true);
    setOpenShowConnections(false);
  };
  const handleOpenEdit = () => {
    setEditOpen(true);
    setOpenShowConnections(false);
  };

  const headers = [
    { key: 'businessName', label: 'Nombre' },
    { key: 'identifier', label: 'Rut' },
    { key: 'statusLabel', label: 'Estado SII', type: 'status-chip' },
    { key: 'enrolledPath', label: 'Origen', type: 'status-chip' },
    { key: 'lastExtraction', label: 'Última extracción', type: 'date-large' },
    { key: 'createdAt', label: 'Creado', type: 'date-large' },
    ...(hasOperativeSummary
      ? [
          {
            key: 'operativeSummary',
            label: 'Resumen operativo',
            customComponent: (
              operativeSummary: BusinessRelation['operativeSummary'],
            ) => <OperativeSummary operativeSummary={operativeSummary} />,
          },
        ]
      : []),
  ];

  if (validatePermissions(user, 'businesses_read_all')) {
    const newList = [{ key: 'users', label: 'Ejecutivos' }];
    headers.push(...newList);
  }

  if (validateCompanyTypes(user, 'Accountant')) {
    const newObject = {
      key: 'vission',
      label: 'Compartiendo',
      type: 'vission',
    };
    headers.push(newObject);
  }

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        type="subHeader"
        title="Estas son las cuentas que has agregado."
        variant="body1"
        buttons={buttonsData}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Tooltip title="Información sobre los estados">
          <IconButton
            size="small"
            onClick={() => setOpenStatusInfoDialog(true)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Chip {...getChipProps(RelationStatus.ACTIVE)} />
        <Chip {...getChipProps(RelationStatus.INACTIVE)} />
        <Chip {...getChipProps(RelationStatus.DEACTIVATED)} />
      </Box>

      <EnhancedTable
        dataHeaders={headers}
        data={filteredBusinesses}
        showActions
        rowsPerPageDefault={50}
        actions={actions}
        exportToFile="xlsx"
        lastElement
      />
      <ConnectBusiness
        setLoading={setLoading}
        dispatch={dispatch}
        setOpenEditDialog={setOpenEditDialog}
        handleInputChange={handleInputChange}
        fetchRelations={fetchRelations}
        selectedBusiness={selectedBusiness}
        openAddDialog={openAddDialog}
        setOpenAddDialog={setOpenAddDialog}
      />

      <EditBusiness
        business={selectedBusiness}
        setLoading={setLoading}
        dispatch={dispatch}
        setOpenEditDialog={setOpenEditDialog}
        openEditDialog={openEditDialog}
        handleInputChange={handleInputChange}
        fetchRelations={fetchRelations}
        selectedBusiness={selectedBusiness}
      />

      {addOpen && (
        <AddRelation
          open={addOpen}
          setOpen={setAddOpen}
          business={selectedBusiness}
          setUp={fetchRelations}
        />
      )}
      {editOpen && (
        <EditConnection
          open={editOpen}
          setOpen={setEditOpen}
          connection={connection}
          setUp={fetchRelations}
        />
      )}
      {openShowConnections && (
        <ShowConnections
          open={openShowConnections}
          business={selectedBusiness}
          setOpen={setOpenShowConnections}
          handleOpenAdd={handleOpenAdd}
          setConnection={setConnection}
          handleOpenEdit={handleOpenEdit}
          setUp={fetchRelations}
        />
      )}
      {openFundDialog && (
        <FundDialog
          open={openFundDialog}
          onClose={() => setOpenFundDialog(false)}
          business={selectedBusiness}
        />
      )}

      <StatusInfoDialog
        open={openStatusInfoDialog}
        onClose={() => setOpenStatusInfoDialog(false)}
      />
      <DatahubReport
        open={openDatahubReportDialog}
        onClose={() => setOpenDatahubReportDialog(false)}
        id={selectedBusiness.id}
      />
    </div>
  );
};

export default Companies;
